/**
 * Shape-aware nesting on an occupancy grid.
 *
 * maxrects packs BOUNDING BOXES, so a C-shaped collar reserves its whole box
 * and nothing can sit in its opening; two sleeves can never interleave. Here
 * every piece is rasterised from its cut silhouette (dilated by half the
 * piece padding plus the laser-outline extent, so gaps and outlines stay
 * physical) onto a grid of CELL_MM cells, and placed at the first position,
 * scanning top-to-bottom then left-to-right, where its real shape fits into
 * the free cells of the sheet. 90° is tried when the factory allows it and
 * the lower placement wins.
 *
 * The output is the same NestResult the sheet builders consume: xPt/yPt are
 * the top-left of the piece's boxed frame (piece + outline extent), exactly as
 * the rectangle packer reported them, so rendering does not change.
 */
import { flattenPathData, IDENTITY } from './clipFlatten';
import ClipperLib from 'clipper-lib';
import {
  DEFAULT_PIECE_PADDING_IN,
  SHEET_EDGE_MARGIN_MM,
  outlineExtentPt,
  resolveOutlineSpec,
  type NestPieceInput,
  type NestPlacement,
  type NestResult,
  type NestSettings,
  type NestSheet,
  type PackOptions,
} from './nesting';
import { inchToPt, mmToPt } from './units';
import { signedArea, type Point, type Polygon } from './labelPath';

/** Grid resolution. 3 mm keeps a 160 × 300 cm sheet at ~540k cells; the
 *  piece padding (3.2 mm default) is still resolved. */
export const CELL_MM = 3;
const CELL_PT = mmToPt(CELL_MM);
const SCALE = 1000;

/** Rasterised footprint of one piece in one orientation. */
export interface PieceMask {
  /** Cells wide / tall (slot = boxed frame + half padding each side). */
  w: number;
  h: number;
  /** Occupied cell offsets, row-major, as packed (dy * w + dx). */
  cells: Int32Array;
  /** Per-row [start, end) runs of occupied cells — the check iterates these. */
  rows: Array<Array<[number, number]>>;
  rotated: boolean;
  /** Slot origin → boxed-frame origin offset (half padding), pt. */
  slotInsetPt: number;
}

interface Grid {
  W: number;
  H: number;
  occ: Uint8Array;
  /** Summed-area table of occ, (W + 1) × (H + 1). */
  sat: Uint32Array;
  satDirty: boolean;
}

function newGrid(W: number, H: number): Grid {
  return {
    W,
    H,
    occ: new Uint8Array(W * H),
    sat: new Uint32Array((W + 1) * (H + 1)),
    satDirty: true,
  };
}

function cloneGrid(g: Grid): Grid {
  return { W: g.W, H: g.H, occ: g.occ.slice(), sat: g.sat.slice(), satDirty: g.satDirty };
}

function rebuildSat(g: Grid): void {
  const { W, H, occ, sat } = g;
  const S = W + 1;
  for (let y = 1; y <= H; y++) {
    let rowSum = 0;
    const rowBase = (y - 1) * W;
    for (let x = 1; x <= W; x++) {
      rowSum += occ[rowBase + x - 1];
      sat[y * S + x] = sat[(y - 1) * S + x] + rowSum;
    }
  }
  g.satDirty = false;
}

function occupiedInBox(g: Grid, x: number, y: number, w: number, h: number): number {
  const S = g.W + 1;
  const { sat } = g;
  return sat[(y + h) * S + (x + w)] - sat[y * S + (x + w)] - sat[(y + h) * S + x] + sat[y * S + x];
}

/**
 * Conservative scanline rasterisation (even-odd over all rings) of polygons
 * given in slot pt coordinates: a cell is occupied when the shape touches ANY
 * part of it (three scanlines per row, floor/ceil bounds), so two masks that
 * do not overlap on the grid are truly at least the dilation apart.
 */
function rasterise(
  polygons: Polygon[],
  w: number,
  h: number
): { cells: number[]; rows: Array<Array<[number, number]>> } {
  const cells: number[] = [];
  const rows: Array<Array<[number, number]>> = [];
  const EPS = 1e-6;
  for (let row = 0; row < h; row++) {
    const covered = new Uint8Array(w);
    for (const frac of [0.02, 0.5, 0.98]) {
      const yc = (row + frac) * CELL_PT;
      const xs: number[] = [];
      for (const poly of polygons) {
        for (let i = 0, n = poly.length; i < n; i++) {
          const [x1, y1] = poly[i];
          const [x2, y2] = poly[(i + 1) % n];
          if (y1 === y2) continue;
          if (yc >= Math.min(y1, y2) && yc < Math.max(y1, y2)) {
            xs.push(x1 + ((yc - y1) * (x2 - x1)) / (y2 - y1));
          }
        }
      }
      xs.sort((a, b) => a - b);
      for (let i = 0; i + 1 < xs.length; i += 2) {
        const a = Math.max(0, Math.floor(xs[i] / CELL_PT + EPS));
        const b = Math.min(w, Math.ceil(xs[i + 1] / CELL_PT - EPS));
        for (let c = a; c < b; c++) covered[c] = 1;
      }
    }
    const runs: Array<[number, number]> = [];
    let c = 0;
    while (c < w) {
      if (!covered[c]) {
        c++;
        continue;
      }
      let e = c;
      while (e < w && covered[e]) e++;
      runs.push([c, e]);
      for (let k = c; k < e; k++) cells.push(row * w + k);
      c = e;
    }
    rows.push(runs);
  }
  return { cells, rows };
}

/** Dilate polygons by `deltaPt` with Clipper (round joins). */
function dilate(polygons: Polygon[], deltaPt: number): Polygon[] {
  if (deltaPt <= 0) return polygons;
  try {
    const subject = polygons.map((p) =>
      p.map(([x, y]) => ({ X: Math.round(x * SCALE), Y: Math.round(y * SCALE) }))
    );
    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
    const unioned: Array<Array<{ X: number; Y: number }>> = [];
    clipper.Execute(
      ClipperLib.ClipType.ctUnion,
      unioned,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );
    const offsetter = new ClipperLib.ClipperOffset(2, 0.2 * SCALE);
    offsetter.AddPaths(unioned, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
    const out: Array<Array<{ X: number; Y: number }>> = [];
    offsetter.Execute(out, deltaPt * SCALE);
    const result = out
      .filter((p) => p.length >= 3)
      .map((p) => p.map((pt) => [pt.X / SCALE, pt.Y / SCALE] as Point));
    return result.length ? result : polygons;
  } catch {
    return polygons;
  }
}

/** Piece silhouette (or its box) as polygons in piece-local pt. */
async function pieceOutlinePolygons(piece: NestPieceInput): Promise<Polygon[]> {
  if (piece.outlineD) {
    const flat = await flattenPathData(piece.outlineD, IDENTITY, 0.3);
    const polys = (flat ?? [])
      .filter((p) => p.points.length >= 3)
      .map((p) => p.points.map(([x, y]) => [x, y] as Point));
    if (polys.length && polys.some((p) => Math.abs(signedArea(p)) > 1)) return polys;
  }
  return [
    [
      [0, 0],
      [piece.widthPt, 0],
      [piece.widthPt, piece.heightPt],
      [0, piece.heightPt],
    ],
  ];
}

/**
 * Masks for one piece: upright and (when allowed) rotated 90°. Slot = boxed
 * frame grown by half the padding on each side; the silhouette is dilated by
 * extent + padding/2 so two masks touching means the outlines are exactly
 * `padding` apart.
 */
export async function buildMasks(
  piece: NestPieceInput,
  extentPt: number,
  paddingPt: number,
  allow90: boolean
): Promise<PieceMask[]> {
  const base = await pieceOutlinePolygons(piece);
  const grow = extentPt + paddingPt / 2;
  const slotW = piece.widthPt + 2 * grow;
  const slotH = piece.heightPt + 2 * grow;
  const shifted = base.map((p) => p.map(([x, y]) => [x + grow, y + grow] as Point));
  const dilated = dilate(shifted, grow);

  const make = (polys: Polygon[], wPt: number, hPt: number, rotated: boolean): PieceMask => {
    const w = Math.max(1, Math.ceil(wPt / CELL_PT));
    const h = Math.max(1, Math.ceil(hPt / CELL_PT));
    const { cells, rows } = rasterise(polys, w, h);
    return { w, h, cells: Int32Array.from(cells), rows, rotated, slotInsetPt: paddingPt / 2 };
  };

  const masks = [make(dilated, slotW, slotH, false)];
  if (allow90) {
    // rotate 90° clockwise within the slot: (x, y) → (slotH - y, x)
    const rot = dilated.map((p) => p.map(([x, y]) => [slotH - y, x] as Point));
    masks.push(make(rot, slotH, slotW, true));
  }
  return masks;
}

function fits(g: Grid, mask: PieceMask, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x + mask.w > g.W || y + mask.h > g.H) return false;
  if (g.satDirty) rebuildSat(g);
  const occupied = occupiedInBox(g, x, y, mask.w, mask.h);
  if (occupied === 0) return true;
  if (occupied > mask.w * mask.h - mask.cells.length) return false;
  const { occ, W } = g;
  for (let row = 0; row < mask.h; row++) {
    const base = (y + row) * W + x;
    for (const [s, e] of mask.rows[row]) {
      for (let c = s; c < e; c++) if (occ[base + c]) return false;
    }
  }
  return true;
}

function stamp(g: Grid, mask: PieceMask, x: number, y: number): void {
  const { occ, W } = g;
  for (let row = 0; row < mask.h; row++) {
    const base = (y + row) * W + x;
    for (const [s, e] of mask.rows[row]) occ.fill(1, base + s, base + e);
  }
  g.satDirty = true;
}

/** Positions within this many mm of the lowest fit count as "equally low";
 *  among them the leftmost wins. Pure bottom-left chases the lowest
 *  millimetre: two curved panel bottoms a few mm apart made the next piece
 *  sit under the lower one, mid-sheet, leaving two strips too narrow for
 *  anything — instead of flush left with one usable column beside it. */
export const BLF_Y_TOLERANCE_MM = 20;
const BLF_Y_TOLERANCE_CELLS = Math.round(BLF_Y_TOLERANCE_MM / CELL_MM);

/** Leftmost fit in the lowest band of rows where the mask fits (see
 *  BLF_Y_TOLERANCE_MM); ties on x go to the lower row. Null when nothing fits. */
function findPosition(
  g: Grid,
  mask: PieceMask,
  tolCells: number,
  xStep = 3
): { x: number; y: number } | null {
  if (g.satDirty) rebuildSat(g);
  const need = mask.cells.length;
  const firstFitInRow = (y: number): number | null => {
    // A band of mask.h rows with fewer free cells than the mask has cells
    // cannot host it anywhere — skip the whole row in O(1).
    if (g.W * mask.h - occupiedInBox(g, 0, y, g.W, mask.h) < need) return null;
    for (let x = 0; x + mask.w <= g.W; x += xStep) {
      if (fits(g, mask, x, y)) {
        // tighten leftwards to the exact first free column
        let bx = x;
        while (bx > 0 && fits(g, mask, bx - 1, y)) bx--;
        return bx;
      }
    }
    return null;
  };
  let best: { x: number; y: number } | null = null;
  for (let y = 0; y + mask.h <= g.H; y++) {
    if (best && y > best.y + tolCells) break;
    const x = firstFitInRow(y);
    if (x === null) continue;
    if (!best) {
      best = { x, y };
      if (x === 0) break;
    } else if (x < best.x) {
      best = { x, y };
      if (x === 0) break;
    }
  }
  return best;
}

interface Prepared {
  piece: NestPieceInput;
  masks: PieceMask[];
  extentPt: number;
}

interface SheetState {
  grid: Grid;
  placements: NestPlacement[];
}

/**
 * Try to place every prepared piece on the sheet; returns the placed subset
 * and the leftovers (mutates the sheet). With `allOrNothing` the sheet is
 * left untouched unless every piece fits.
 */
function placeOnSheet(
  sheet: SheetState,
  items: Prepared[],
  borderCells: number,
  paperWidthPt: number,
  paperLengthPt: number,
  allOrNothing: boolean,
  garmentSequence: boolean,
  tolCells: number
): { placed: Prepared[]; left: Prepared[] } {
  const work = allOrNothing
    ? { grid: cloneGrid(sheet.grid), placements: [...sheet.placements] }
    : sheet;
  const placed: Prepared[] = [];
  const left: Prepared[] = [];
  // garmentSequence: once a piece of garment k misses, the REST of garment k
  // still gets its chance on this sheet (small pieces fill the gaps the big
  // one could not), but garment k+1 onwards waits for the next sheet so
  // garments stay contiguous and in order.
  let missedGarment: number | null = null;
  for (const item of items) {
    const garment = item.piece.copyIndex ?? 0;
    if (garmentSequence && missedGarment !== null && garment !== missedGarment) {
      left.push(...items.slice(items.indexOf(item)));
      break;
    }
    let best: { x: number; y: number; mask: PieceMask } | null = null;
    // (sync loop; the caller yields between sheets/garments via nestPieces)
    for (const mask of item.masks) {
      const pos = findPosition(work.grid, mask, tolCells);
      if (!pos) continue;
      if (!best || pos.y < best.y - tolCells || (pos.y <= best.y + tolCells && pos.x < best.x)) {
        best = { ...pos, mask };
      }
    }
    if (!best) {
      if (allOrNothing) return { placed: [], left: items };
      if (garmentSequence) missedGarment = garment;
      left.push(item);
      continue;
    }
    stamp(work.grid, best.mask, best.x, best.y);
    // slot cell origin → boxed frame origin (pt); the grid origin sits at the
    // sheet border, so add it back.
    const borderPt = borderCells * CELL_PT;
    const xPt = borderPt + best.x * CELL_PT + best.mask.slotInsetPt;
    const yPt = borderPt + best.y * CELL_PT + best.mask.slotInsetPt;
    work.placements.push({ piece: item.piece, xPt, yPt, rotated: best.mask.rotated });
    placed.push(item);
  }
  if (allOrNothing) {
    sheet.grid = work.grid;
    sheet.placements = work.placements;
  }
  void paperWidthPt;
  void paperLengthPt;
  return { placed, left };
}

function toSheet(state: SheetState, sheetAreaPt2: number): NestSheet {
  const used = state.placements.reduce(
    (sum, p) => sum + (p.piece.areaPt2 ?? p.piece.widthPt * p.piece.heightPt),
    0
  );
  const garments = [
    ...new Set(
      state.placements
        .map((p) => p.piece.copyIndex)
        .filter((c): c is number => c !== null && c !== undefined)
    ),
  ].sort((a, b) => a - b);
  return {
    placements: state.placements,
    utilization: sheetAreaPt2 > 0 ? used / sheetAreaPt2 : 0,
    garments: garments.length ? garments : undefined,
  };
}

/**
 * Shape-aware replacement for packPieces(). Same contract and options; async
 * because silhouettes are flattened with paper.js.
 */
export async function nestPieces(
  pieces: NestPieceInput[],
  settings: NestSettings,
  paddingIn?: number,
  options: PackOptions = {}
): Promise<NestResult> {
  const effectivePaddingIn = paddingIn ?? settings.piece_padding ?? DEFAULT_PIECE_PADDING_IN;
  const paddingPt = inchToPt(effectivePaddingIn);
  const allow90 = settings.allowed_rotations?.includes(90) ?? false;
  const outlineSpec = resolveOutlineSpec(settings);
  const paperWidthPt = inchToPt(settings.paper_width);
  const paperLengthPt = inchToPt(settings.paper_length);
  const borderPt = mmToPt(SHEET_EDGE_MARGIN_MM);
  const borderCells = Math.ceil(borderPt / CELL_PT);
  const W = Math.floor(paperWidthPt / CELL_PT) - 2 * borderCells;
  const H = Math.floor(paperLengthPt / CELL_PT) - 2 * borderCells;
  const sheetArea = paperWidthPt * paperLengthPt;
  const oversized: NestPieceInput[] = [];
  const prepared: Prepared[] = [];
  const maskMemo = new Map<string, Promise<PieceMask[]>>();
  for (const piece of pieces) {
    const extentPt = outlineExtentPt(piece, outlineSpec);
    const key = `${piece.outlineD ?? 'box'}|${piece.widthPt}|${piece.heightPt}|${extentPt}`;
    let pending = maskMemo.get(key);
    if (!pending) {
      pending = buildMasks(piece, extentPt, paddingPt, allow90);
      maskMemo.set(key, pending);
    }
    const masks = (await pending).filter((m) => m.w <= W && m.h <= H);
    if (!masks.length) {
      oversized.push(piece);
      continue;
    }
    prepared.push({ piece, masks, extentPt });
  }
  const areaOf = (p: Prepared) => p.piece.areaPt2 ?? p.piece.widthPt * p.piece.heightPt;
  const byAreaDesc = (a: Prepared, b: Prepared) => areaOf(b) - areaOf(a);

  const sheets: NestSheet[] = [];
  const fresh = (): SheetState => ({ grid: newGrid(W, H), placements: [] });

  // Let the modal repaint between sheets (a big order takes seconds).
  // MessageChannel, not setTimeout: Chrome throttles chained timers in a
  // hidden tab to one wake per minute, which turned a 8 s nest into a
  // 6-minute one when the user switched tabs while it ran.
  const yieldToUi = () =>
    new Promise<void>((resolve) => {
      if (typeof MessageChannel === 'undefined') return void setTimeout(resolve, 0);
      const channel = new MessageChannel();
      channel.port1.onmessage = () => resolve();
      channel.port2.postMessage(null);
    });
  /** One packing pass over `items` with a given bottom-left tolerance. */
  const packRun = async (
    items: Prepared[],
    garmentSets: boolean,
    group: string | null | undefined,
    tolCells: number
  ): Promise<{ sheets: NestSheet[]; oversized: NestPieceInput[] }> => {
    const out = { sheets: [] as NestSheet[], oversized: [] as NestPieceInput[] };
    if (!garmentSets) {
      let remaining = [...items].sort(byAreaDesc);
      while (remaining.length) {
        await yieldToUi();
        const sheet = fresh();
        const { placed, left } = placeOnSheet(
          sheet,
          remaining,
          borderCells,
          paperWidthPt,
          paperLengthPt,
          false,
          false,
          tolCells
        );
        if (!placed.length) {
          out.oversized.push(...left.map((l) => l.piece));
          break;
        }
        out.sheets.push({ ...toSheet(sheet, sheetArea), group });
        remaining = left;
      }
      return out;
    }
    // Garments in order, each sheet filled as far as it goes: garment 1, then
    // as much of garment 2 as still fits (every piece of it is tried, so the
    // small ones fill the gaps the big one could not), the rest of it opens
    // the next sheet. Garments stay contiguous (never scattered by size
    // across sheets) while no sheet is left half empty for the sake of a
    // whole set.
    const byGarment = new Map<number, Prepared[]>();
    for (const item of items) {
      const k = item.piece.copyIndex ?? 0;
      const list = byGarment.get(k);
      if (list) list.push(item);
      else byGarment.set(k, [item]);
    }
    const ordered = [...byGarment.entries()]
      .sort((a, b) => a[0] - b[0])
      .flatMap(([, l]) => l.sort(byAreaDesc));
    let sheet: SheetState = fresh();
    let remaining = ordered;
    while (remaining.length) {
      await yieldToUi();
      const r = placeOnSheet(
        sheet,
        remaining,
        borderCells,
        paperWidthPt,
        paperLengthPt,
        false,
        true,
        tolCells
      );
      if (!r.placed.length) {
        // nothing of what is left fits an empty sheet
        if (!sheet.placements.length) {
          out.oversized.push(...r.left.map((l) => l.piece));
          break;
        }
        out.sheets.push({ ...toSheet(sheet, sheetArea), group });
        sheet = fresh();
        continue;
      }
      remaining = r.left;
      if (remaining.length) {
        out.sheets.push({ ...toSheet(sheet, sheetArea), group });
        sheet = fresh();
      }
    }
    if (sheet.placements.length) out.sheets.push({ ...toSheet(sheet, sheetArea), group });
    return out;
  };

  // Two passes: tolerant bottom-left (flush-left within BLF_Y_TOLERANCE_MM,
  // no slivers) and strict bottom-left. Neither dominates — the tolerant pass
  // saved a sheet on a per-row XL run, the strict one on a whole-order run —
  // so the pass with fewer sheets wins (tie → tolerant, tidier sheets). The
  // masks are built once; a pass is placement only.
  const packBest = async (items: Prepared[], garmentSets: boolean, group?: string | null) => {
    const tolerant = await packRun(items, garmentSets, group, BLF_Y_TOLERANCE_CELLS);
    if (tolerant.sheets.length <= 1) return tolerant;
    const strict = await packRun(items, garmentSets, group, 0);
    return strict.sheets.length < tolerant.sheets.length ? strict : tolerant;
  };
  const take = (r: { sheets: NestSheet[]; oversized: NestPieceInput[] }) => {
    sheets.push(...r.sheets);
    oversized.push(...r.oversized);
  };
  if (!options.perRow) {
    take(await packBest(prepared, false));
  } else {
    const byRow = new Map<number, Prepared[]>();
    for (const item of prepared) {
      const list = byRow.get(item.piece.rowIndex);
      if (list) list.push(item);
      else byRow.set(item.piece.rowIndex, [item]);
    }
    for (const [rowIndex, rowItems] of byRow) {
      const label =
        options.rowLabels?.get(rowIndex) ??
        `row ${rowIndex + 1}${rowItems[0]?.piece.size ? ` · ${rowItems[0].piece.size}` : ''}`;
      take(await packBest(rowItems, true, label));
    }
  }

  return { sheets, oversized, paperWidthPt, paperLengthPt, paddingPt };
}
